import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import Croppie from 'croppie';
import { ImageCropperExtraService } from './image-cropper-extra.service';
import { IMAGE_FILE_FORMATS, SentryLoggerService } from '@cakeiteasy/web-utils';

const logContext = 'ImageCropperExtra';

@Component({
  standalone: false,
  selector: 'image-cropper-extra',
  templateUrl: 'image-cropper-extra.component.html',
  styleUrls: ['image-cropper-extra.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class ImageCropperExtraComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() imgUrl: string;
  @Input() fitIntoAspectRatio: boolean;
  @Input() keepBgTransparent: boolean = false;

  @Input() enableResize: boolean;
  @Input() circle: boolean;

  @Input() longSize: number = 1024;

  @Input() noAspectRatio = false;

  @Input() aspectRatio: number = 1;
  @Input() format: IMAGE_FILE_FORMATS = IMAGE_FILE_FORMATS.JPG;

  @Output() onCropped = new EventEmitter<string>();

  @ViewChild('cropperContainer', { static: true }) container: ElementRef<HTMLDivElement>;
  @ViewChild('cropper', { static: true }) cropper: ElementRef;

  public containerHeight: string;

  private _croppie: any;
  private _image: HTMLImageElement;
  private _alreadyFittedIntoAspectRatio: boolean;

  private _defaultImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/4/47/PNG_transparency_demonstration_1.png';

  public constructor(private _extraCropper: ImageCropperExtraService, private _logger: SentryLoggerService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    const { imgUrl, aspectRatio, circle, fitIntoAspectRatio } = changes;

    if (imgUrl) {
      const updImgUrl = imgUrl.currentValue;
      if (updImgUrl) {
        this._alreadyFittedIntoAspectRatio = false;
        this.changeImageTo(updImgUrl);
      }
    } else if ((aspectRatio || circle || fitIntoAspectRatio) && this.imgUrl) {
      this._alreadyFittedIntoAspectRatio = false;
      this.changeImageTo(this.imgUrl);
    }
  }

  ngAfterViewInit() {
    if (!this.imgUrl) {
      this.loadDefaultImage();
    }
  }

  loadDefaultImage() {
    this.imgUrl = this._defaultImageUrl;
    this.changeImageTo(this._defaultImageUrl);
  }

  changeImageTo(src: string) {
    this.container.nativeElement.classList.remove('loaded');

    if (this._croppie) {
      this._croppie.destroy();
      this._croppie.options.update = null;
      this._croppie = null;
    }

    this.cropper.nativeElement.innerHTML = '';

    // setting timeout to handle inner html clearing
    setTimeout(() => {
      const img = document.createElement('img');
      img.style.opacity = '0';
      img.style.width = '100%';
      img.style.height = 'auto';
      this.cropper.nativeElement.appendChild(img);

      this._image = img;

      this._image.onload = () => {
        const w = this._image.naturalWidth;
        const h = this._image.naturalHeight;
        const aspectRatio = this.aspectRatio;

        console.log(logContext, 'image loaded with', JSON.stringify({
          naturalWidth: w,
          naturalHeight: h,
          aspectRatio
        }));

        if (!this.circle && this.fitIntoAspectRatio && aspectRatio && !this._alreadyFittedIntoAspectRatio &&
          Math.abs(w / h - aspectRatio) >= 0.0005) {

          console.log(logContext, 'fitting into aspect ratio', JSON.stringify({
            newAspectRatio: 1 / aspectRatio
          }));

          this._alreadyFittedIntoAspectRatio = true;

          this.changeImageTo(this._extraCropper.fitAspectRatio(img, 1 / aspectRatio, this.format, this.keepBgTransparent));
          this._image.onload = null;
          return;
        }

        this.initCropper(this._image);
      };
      img.src = src;
    });
  }


  initCropper(img: HTMLImageElement): void {
    let w = img.naturalWidth || img.clientWidth;
    let h = img.naturalHeight || img.clientHeight;

    const longSize = Math
      .min(this.container.nativeElement.getBoundingClientRect().width - 10, 450);
    if (this.aspectRatio && this.aspectRatio > 1) {
      h = longSize;
      w = h / this.aspectRatio;
    } else {
      w = longSize;
      h = w * this.aspectRatio;
    }

    let loaded = false;
    let updateTimeout = null;

    const resultZoom = 400;
    const maxZoom = img.naturalWidth * w / img.naturalWidth / resultZoom;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    this.containerHeight = '';
    this._updateContainerHeightIfNecessary(img.clientHeight)
      .then(() => {
        console.log(logContext, 'initializing croppie', {
          width: w,
          height: h,
          maxZoom: maxZoom,
          isCircle: this.circle
        });

        this._croppie = new Croppie(img, {
          circle: true,
          enableExif: true,
          maxZoom: maxZoom,
          enableResize: this.enableResize,
          viewport: {
            width: w,
            height: h,
            type: this.circle ? 'circle' : 'square'
          },
          update: (function () {
            if (loaded === false) {
              loaded = true;
              this._croppie.setZoom(0);
              this.container.nativeElement.classList.add('loaded');
            }

            if (updateTimeout) {
              clearTimeout(updateTimeout);
            }

            this.onCropped.emit(null);

            updateTimeout = setTimeout(() => {
              // in case cropper was in new init state
              if (!this._croppie) {
                return;
              }

              const size: any = {
                [w > h ? 'width' : 'height']: Math.min(this.longSize || 1024, Math.max(naturalWidth, naturalHeight))
              };

              console.log(logContext, 'getting cropped image', {
                width: w,
                height: h,
                maxSize: this.longSize || 1024,
                targetSize: size
              });

              let croppieFormat = this.format === IMAGE_FILE_FORMATS.JPG ? 'jpeg' : 'png';

              // make it png to have transparent bg and then make it white
              if (this.circle) {
                croppieFormat = 'png';
              }

              this._croppie.result({
                type: 'base64',
                size,
                format: croppieFormat,
                quality: 1
              })
                .then((img: string) => {
                  console.log(logContext, 'got cropped image', {
                    targetSize: size
                  });

                  this._extraCropper.checkIfImageIsEmpty(img, 'Image cropper');

                  if (this.circle) {
                    const resultSideLength = size.width || size.height;
                    return this._addWhiteBackground(img, resultSideLength, resultSideLength)
                      .then((base64Str) => this.onCropped.emit(base64Str));
                  } else {
                    this.onCropped.emit(img);
                  }
                });
            }, 100);
          }).bind(this)
        });
      });
  }

  private _updateContainerHeightIfNecessary(imgClientHeight: number): Promise<void> {
    return new Promise((res, rej) => {
      if ((this.enableResize || this.fitIntoAspectRatio) && imgClientHeight >= this.container.nativeElement.clientHeight) {
        this.containerHeight = imgClientHeight + 'px';
      }
      setTimeout(() => {
        res(null);
      }, 10);
    });
  }

  _addWhiteBackground(img: string, w: number, h: number): Promise<string> {
    return new Promise((res, rej) => {
      const image = new Image();
      image.src = img;
      image.onload = () => {
        const c = this._extraCropper.getCanvas({ fillColor: '#ffffff', width: w, height: h });
        const ctx = c.getContext('2d');
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(image, 0, 0, w, h);

        console.log(logContext, 'putting image on white bg', JSON.stringify({
          w,
          h,
        }));

        res(c.toDataURL(IMAGE_FILE_FORMATS.JPG, 100));
      };
    });
  }


  ngOnDestroy() {
    this._croppie.destroy();
  }
}
